import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios"
import { getAccessToken, refreshToken, clearAuth } from "@/stores/auth"

export const apiClient: AxiosInstance = axios.create({
  baseURL: "/api/v1",
  headers: { "Content-Type": "application/json", Accept: "application/json" },
  timeout: 15000,
})

/** Decode the `exp` claim without verifying the signature. */
function isExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]))
    // Refresh 30 s before actual expiry so overlapping requests don't 401
    return payload.exp * 1000 < Date.now() + 30_000
  } catch {
    return true
  }
}

let refreshing: Promise<string | null> | null = null

/** Proactively refresh an expired access token before the request leaves the
 *  browser. This eliminates the 401 → retry round-trip that fires on every
 *  page load when the stored token has expired (15-min TTL). */
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  let token = getAccessToken()
  if (token && isExpired(token)) {
    refreshing ??= refreshToken().finally(() => { refreshing = null })
    token = await refreshing
  }
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`)
  }
  return config
})

/*
 * When the auth interceptor gives up (refresh failed → tokens cleared),
 * the user has to land somewhere. We can't import vue-router here without
 * creating a circular dependency, so the application bootstrap installs
 * the redirect handler. See entrypoints/application.ts.
 */
let onSessionLost: () => void = () => { /* installed at boot */ }
export function setSessionLostHandler(fn: () => void): void {
  onSessionLost = fn
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    if (error.response?.status === 401 && !original?._retry) {
      original._retry = true
      refreshing ??= refreshToken().finally(() => { refreshing = null })
      const newToken = await refreshing
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`
        return apiClient(original)
      }
      clearAuth()
      onSessionLost()
    }
    return Promise.reject(error)
  },
)
